import { apiFetch } from "../../lib/api";
import { useCallback, useEffect, useMemo, useState } from "react";

// ── Types ─────────────────────────────────────────────────────

type RiskLevel = "low" | "medium" | "high" | "critical";
type ReviewStatus = "pending" | "approved" | "rejected" | "applied";

interface QuarantineEntry {
  id: string;
  file_path: string;
  original_content: string;
  proposed_content: string;
  diff_preview: string;
  confidence: number;
  risk_level: RiskLevel;
  status: ReviewStatus;
  provider: string;
  model: string;
  prompt_preview: string;
  created_at: string;
}

type Tab = "queue" | "history" | "stats";

// ── Palette ───────────────────────────────────────────────────

const RISK_COLORS: Record<RiskLevel, string> = {
  low: "#4f8fff",
  medium: "#fbbf24",
  high: "#ffb86c",
  critical: "#ff5555",
};

const STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: "PENDING",
  approved: "APPROVED",
  rejected: "REJECTED",
  applied: "APPLIED",
};

// ── Helpers ───────────────────────────────────────────────────

function riskBadge(level: RiskLevel) {
  return (
    <span
      className="q-risk-badge"
      style={{
        color: "#0d0d0d",
        backgroundColor: RISK_COLORS[level],
        fontWeight: 700,
        fontSize: "0.65rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: "3px",
      }}
    >
      {level}
    </span>
  );
}

function confidenceBar(value: number) {
  const pct = Math.round(value * 100);
  const color =
    pct >= 90 ? "#4f8fff" : pct >= 70 ? "#fbbf24" : pct >= 50 ? "#ffb86c" : "#ff5555";
  return (
    <div className="q-confidence" style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 80,
          height: 6,
          backgroundColor: "rgba(255,255,255,0.06)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: 3,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <span style={{ color, fontSize: "0.75rem", fontFamily: "monospace" }}>{pct}%</span>
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Component ─────────────────────────────────────────────────

export function QuarantineWorkbench() {
  const [tab, setTab] = useState<Tab>("queue");
  const [pending, setPending] = useState<QuarantineEntry[]>([]);
  const [history, setHistory] = useState<QuarantineEntry[]>([]);
  const [selected, setSelected] = useState<QuarantineEntry | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Data fetching ─────────────────────────────────────────

  const fetchPending = useCallback(async () => {
    try {
      const res = await apiFetch("/api/quarantine");
      const data = await res.json();
      setPending(data.items ?? []);
    } catch {
      /* offline */
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await apiFetch("/api/quarantine/history");
      const data = await res.json();
      setHistory(data.items ?? []);
    } catch {
      /* offline */
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchPending(), fetchHistory()]).then(() => setLoading(false));
  }, [fetchPending, fetchHistory]);

  // SSE live updates
  useEffect(() => {
    const es = new EventSource("/api/events");
    const handler = () => {
      fetchPending();
      fetchHistory();
    };
    es.addEventListener("quarantine-new", handler);
    es.addEventListener("quarantine-approved", handler);
    es.addEventListener("quarantine-rejected", handler);
    return () => es.close();
  }, [fetchPending, fetchHistory]);

  // ── Actions ───────────────────────────────────────────────

  const approve = useCallback(
    async (id: string) => {
      await apiFetch(`/api/quarantine/${id}/approve`, { method: "POST" });
      await Promise.all([fetchPending(), fetchHistory()]);
      setSelected((prev) => (prev?.id === id ? { ...prev, status: "approved" } : prev));
    },
    [fetchPending, fetchHistory],
  );

  const reject = useCallback(
    async (id: string) => {
      await apiFetch(`/api/quarantine/${id}/reject`, { method: "POST" });
      await Promise.all([fetchPending(), fetchHistory()]);
      setSelected((prev) => (prev?.id === id ? { ...prev, status: "rejected" } : prev));
    },
    [fetchPending, fetchHistory],
  );

  // ── Stats ─────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = history.length;
    const approved = history.filter((e) => e.status === "approved" || e.status === "applied").length;
    const rejected = history.filter((e) => e.status === "rejected").length;
    const byRisk: Record<RiskLevel, number> = { low: 0, medium: 0, high: 0, critical: 0 };
    for (const entry of history) {
      byRisk[entry.risk_level]++;
    }
    return {
      total,
      approved,
      rejected,
      approvalRate: total > 0 ? Math.round((approved / total) * 100) : 0,
      byRisk,
    };
  }, [history]);

  // ── Render ────────────────────────────────────────────────

  return (
    <main
      className="quarantine-workbench"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#0d0d0d",
        color: "#f8f8f2",
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="#4f8fff"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span style={{ fontSize: "0.95rem", fontWeight: 700, color: "#4f8fff" }}>
            AI Code Quarantine
          </span>
          <span
            style={{
              fontSize: "0.65rem",
              color: "#6272a4",
              border: "1px solid rgba(255,255,255,0.06)",
              padding: "1px 6px",
              borderRadius: 3,
            }}
          >
            {pending.length} pending
          </span>
        </div>

        {/* Tabs */}
        <nav style={{ display: "flex", gap: 2 }}>
          {(["queue", "history", "stats"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setSelected(null);
              }}
              style={{
                padding: "4px 14px",
                fontSize: "0.72rem",
                fontFamily: "inherit",
                fontWeight: tab === t ? 700 : 400,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: tab === t ? "#0d0d0d" : "#4f8fff",
                backgroundColor: tab === t ? "#4f8fff" : "transparent",
                border: tab === t ? "none" : "1px solid rgba(255,255,255,0.06)",
                borderRadius: 3,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {t}
            </button>
          ))}
        </nav>
      </header>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* ── Queue Tab ────────────────────────────────────── */}
        {tab === "queue" && (
          <>
            {/* List */}
            <div
              style={{
                width: selected ? "38%" : "100%",
                borderRight: selected ? "1px solid rgba(255,255,255,0.06)" : "none",
                overflowY: "auto",
                transition: "width 0.2s ease",
              }}
            >
              {loading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#6272a4" }}>
                  Loading quarantine queue...
                </div>
              ) : pending.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 12,
                    color: "#6272a4",
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width={32}
                    height={32}
                    fill="none"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={1.5}
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <span style={{ fontSize: "0.8rem" }}>No pending items. All clear.</span>
                </div>
              ) : (
                pending.map((entry) => (
                  <button
                    key={entry.id}
                    onClick={() => setSelected(entry)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      width: "100%",
                      padding: "14px 20px",
                      textAlign: "left",
                      backgroundColor:
                        selected?.id === entry.id ? "#1a1b26" : "transparent",
                      borderBottom: "1px solid #1e1e2e",
                      border: "none",
                      borderLeft:
                        selected?.id === entry.id
                          ? `2px solid ${RISK_COLORS[entry.risk_level]}`
                          : "2px solid transparent",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      color: "inherit",
                      transition: "all 0.12s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#f8f8f2",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "70%",
                        }}
                      >
                        {entry.file_path}
                      </span>
                      {riskBadge(entry.risk_level)}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: "0.68rem",
                        color: "#6272a4",
                      }}
                    >
                      <span>{entry.id}</span>
                      <span>{entry.provider}/{entry.model}</span>
                      {confidenceBar(entry.confidence)}
                      <span style={{ marginLeft: "auto" }}>{relativeTime(entry.created_at)}</span>
                    </div>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: "#6272a4",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {entry.prompt_preview}
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Detail panel */}
            {selected && (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Detail header */}
                <div
                  style={{
                    padding: "14px 20px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#f8f8f2" }}>
                      {selected.file_path}
                    </span>
                    <button
                      onClick={() => setSelected(null)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#6272a4",
                        cursor: "pointer",
                        fontSize: "1rem",
                        fontFamily: "inherit",
                      }}
                    >
                      x
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      fontSize: "0.7rem",
                      color: "#6272a4",
                    }}
                  >
                    {riskBadge(selected.risk_level)}
                    {confidenceBar(selected.confidence)}
                    <span>
                      {selected.provider}/{selected.model}
                    </span>
                    <span>{relativeTime(selected.created_at)}</span>
                  </div>
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "#a8b2b8",
                      fontStyle: "italic",
                    }}
                  >
                    &quot;{selected.prompt_preview}&quot;
                  </div>
                </div>

                {/* Diff preview */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "12px 20px",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontSize: "0.72rem",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      color: "#f8f8f2",
                    }}
                  >
                    {selected.diff_preview.split("\n").map((line, i) => {
                      let color = "#f8f8f2";
                      if (line.startsWith("+")) color = "#4f8fff";
                      else if (line.startsWith("-")) color = "#ff5555";
                      else if (line.startsWith("@@")) color = "#bd93f9";
                      return (
                        <span key={i} style={{ color, display: "block" }}>
                          {line}
                        </span>
                      );
                    })}
                  </pre>
                </div>

                {/* Action bar */}
                {selected.status === "pending" && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      padding: "12px 20px",
                      borderTop: "1px solid rgba(255,255,255,0.06)",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={() => reject(selected.id)}
                      style={{
                        flex: 1,
                        padding: "8px 0",
                        fontSize: "0.75rem",
                        fontFamily: "inherit",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#ff5555",
                        backgroundColor: "transparent",
                        border: "1px solid #ff555533",
                        borderRadius: 4,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => approve(selected.id)}
                      style={{
                        flex: 1,
                        padding: "8px 0",
                        fontSize: "0.75rem",
                        fontFamily: "inherit",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#0d0d0d",
                        backgroundColor: "#4f8fff",
                        border: "none",
                        borderRadius: 4,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Approve
                    </button>
                  </div>
                )}

                {selected.status !== "pending" && (
                  <div
                    style={{
                      padding: "12px 20px",
                      borderTop: "1px solid rgba(255,255,255,0.06)",
                      fontSize: "0.72rem",
                      color: "#6272a4",
                      textAlign: "center",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      flexShrink: 0,
                    }}
                  >
                    {STATUS_LABELS[selected.status]}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── History Tab ──────────────────────────────────── */}
        {tab === "history" && (
          <div style={{ flex: 1, overflowY: "auto" }}>
            {history.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#6272a4" }}>
                No review history yet.
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.72rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      color: "#6272a4",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontSize: "0.65rem",
                    }}
                  >
                    <th style={{ padding: "10px 16px", textAlign: "left" }}>ID</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>File</th>
                    <th style={{ padding: "10px 8px", textAlign: "center" }}>Risk</th>
                    <th style={{ padding: "10px 8px", textAlign: "center" }}>Confidence</th>
                    <th style={{ padding: "10px 8px", textAlign: "center" }}>Status</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>Provider</th>
                    <th style={{ padding: "10px 16px", textAlign: "right" }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => (
                    <tr
                      key={entry.id}
                      onClick={() => {
                        setSelected(entry);
                        setTab("queue");
                      }}
                      style={{
                        borderBottom: "1px solid #1e1e2e",
                        cursor: "pointer",
                        transition: "background 0.1s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#1a1b26")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "transparent")
                      }
                    >
                      <td
                        style={{
                          padding: "10px 16px",
                          fontFamily: "monospace",
                          color: "#6272a4",
                        }}
                      >
                        {entry.id}
                      </td>
                      <td
                        style={{
                          padding: "10px 8px",
                          color: "#f8f8f2",
                          maxWidth: 240,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.file_path}
                      </td>
                      <td style={{ padding: "10px 8px", textAlign: "center" }}>
                        {riskBadge(entry.risk_level)}
                      </td>
                      <td style={{ padding: "10px 8px" }}>{confidenceBar(entry.confidence)}</td>
                      <td
                        style={{
                          padding: "10px 8px",
                          textAlign: "center",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          color:
                            entry.status === "approved" || entry.status === "applied"
                              ? "#4f8fff"
                              : entry.status === "rejected"
                                ? "#ff5555"
                                : "#fbbf24",
                        }}
                      >
                        {entry.status}
                      </td>
                      <td style={{ padding: "10px 8px", color: "#6272a4" }}>
                        {entry.provider}/{entry.model}
                      </td>
                      <td
                        style={{
                          padding: "10px 16px",
                          textAlign: "right",
                          color: "#6272a4",
                        }}
                      >
                        {relativeTime(entry.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Stats Tab ────────────────────────────────────── */}
        {tab === "stats" && (
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 16,
              padding: 20,
              alignContent: "start",
            }}
          >
            {/* Approval rate */}
            <div
              style={{
                backgroundColor: "#1a1b26",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 6,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.65rem",
                  color: "#6272a4",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Approval Rate
              </span>
              <span
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: stats.approvalRate >= 70 ? "#4f8fff" : "#fbbf24",
                }}
              >
                {stats.approvalRate}%
              </span>
              <span style={{ fontSize: "0.7rem", color: "#6272a4" }}>
                {stats.approved} approved / {stats.total} total
              </span>
            </div>

            {/* Total reviewed */}
            <div
              style={{
                backgroundColor: "#1a1b26",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 6,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.65rem",
                  color: "#6272a4",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Total Reviewed
              </span>
              <span style={{ fontSize: "2rem", fontWeight: 700, color: "#f8f8f2" }}>
                {stats.total}
              </span>
              <span style={{ fontSize: "0.7rem", color: "#6272a4" }}>
                {stats.rejected} rejected
              </span>
            </div>

            {/* Pending */}
            <div
              style={{
                backgroundColor: "#1a1b26",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 6,
                padding: 20,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.65rem",
                  color: "#6272a4",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Pending Review
              </span>
              <span
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: pending.length > 0 ? "#fbbf24" : "#4f8fff",
                }}
              >
                {pending.length}
              </span>
              <span style={{ fontSize: "0.7rem", color: "#6272a4" }}>
                items awaiting decision
              </span>
            </div>

            {/* By risk level */}
            {(["low", "medium", "high", "critical"] as RiskLevel[]).map((level) => (
              <div
                key={level}
                style={{
                  backgroundColor: "#1a1b26",
                  border: "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 6,
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: "0.65rem",
                    color: "#6272a4",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {level} Risk
                </span>
                <span
                  style={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: RISK_COLORS[level],
                  }}
                >
                  {stats.byRisk[level]}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: RISK_COLORS[level],
                    }}
                  />
                  <span style={{ fontSize: "0.7rem", color: "#6272a4" }}>
                    {stats.total > 0
                      ? `${Math.round((stats.byRisk[level] / stats.total) * 100)}%`
                      : "0%"}{" "}
                    of total
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
